/**
 * Integrations Types & Interfaces
 *
 * Ported from EBTimeEntry for the new table-based integrations UI.
 * Models the simplified integration setup (product + externalInstanceId)
 * and user-to-company access management.
 */

// ---------------------------------------------------------------------------
// Status Types
// ---------------------------------------------------------------------------

export type CompanyStatus = 'active' | 'inactive' | 'setup';

export type IntegrationConnectionStatus =
  | 'connected'
  | 'not_connected'
  | 'error'
  | 'testing'
  | 'not_configured';

// ---------------------------------------------------------------------------
// Integration Product Options
// ---------------------------------------------------------------------------

export type FinancialProduct =
  | 'access-financials'
  | 'access-dimensions'
  | 'xero'
  | 'sage'
  | 'quickbooks'
  | 'other';

export type HRProduct =
  | 'people-hr'
  | 'access-time-hr'
  | 'bamboohr'
  | 'adp'
  | 'paycircle'
  | 'workday'
  | 'other';

export type ProjectProduct =
  | 'access-projects'
  | 'access-focalpoint'
  | 'other';

export type PlannerProduct =
  | 'access-resource-scheduler'
  | 'access-planner'
  | 'other';

export type TimesheetProduct =
  | 'access-time'
  | 'access-focalpoint'
  | 'other';


export const FINANCIAL_PRODUCT_LABELS: Record<FinancialProduct, string> = {
  'access-financials': 'Access Financials',
  'access-dimensions': 'Access Dimensions',
  xero: 'Xero',
  sage: 'Sage',
  quickbooks: 'QuickBooks',
  other: 'Other',
};

export const HR_PRODUCT_LABELS: Record<HRProduct, string> = {
  'people-hr': 'People HR',
  'access-time-hr': 'Access Time (Timesheet)',
  bamboohr: 'BambooHR',
  adp: 'ADP',
  paycircle: 'Paycircle',
  workday: 'Workday',
  other: 'Other',
};

export const PROJECT_PRODUCT_LABELS: Record<ProjectProduct, string> = {
  'access-projects': 'Access Projects',
  'access-focalpoint': 'Access FocalPoint',
  other: 'Other',
};

export const PLANNER_PRODUCT_LABELS: Record<PlannerProduct, string> = {
  'access-resource-scheduler': 'Resource Scheduler',
  'access-planner': 'Scheduler',
  other: 'Other',
};

export const TIMESHEET_PRODUCT_LABELS: Record<TimesheetProduct, string> = {
  'access-time': 'Access Time',
  'access-focalpoint': 'Access FocalPoint',
  other: 'Other',
};


// ---------------------------------------------------------------------------
// Integration Config
// ---------------------------------------------------------------------------

export interface SimpleIntegrationConfig {
  product: FinancialProduct | HRProduct | ProjectProduct | PlannerProduct | TimesheetProduct | null;
  externalInstanceId: string | null;
  /** Secondary external instance ID (e.g. EBProjects numeric CompanyId for access-projects) */
  externalInstanceIdSecondary?: string | null;
  /** Display name of the connected external instance (persisted so the UI can show it on re-open) */
  instanceName?: string | null;
  status: IntegrationConnectionStatus;
  lastSyncAt: string | null;
}

// ---------------------------------------------------------------------------
// Company with Integrations
// ---------------------------------------------------------------------------

export interface CompanyWithIntegrations {
  id: string;
  name: string;
  code: string;
  status: CompanyStatus;
  financialIntegration: SimpleIntegrationConfig;
  hrIntegration: SimpleIntegrationConfig;
  projectIntegration: SimpleIntegrationConfig;
  plannerIntegration: SimpleIntegrationConfig;
  timesheetIntegration: SimpleIntegrationConfig;
  userCount: number;
}

// ---------------------------------------------------------------------------
// Company User
// ---------------------------------------------------------------------------

export interface CompanyUser {
  id: string;
  name: string;
  email: string;
  role: string;
  photoUrl?: string;
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// Employee Company Access
// ---------------------------------------------------------------------------

export interface EmployeeCompanyAccessRow {
  companyId: string;
  companyName: string;
  companyCode: string;
  companyStatus: CompanyStatus;
  hasAccess: boolean;
  isDefault: boolean;
}

// ---------------------------------------------------------------------------
// API Payloads
// ---------------------------------------------------------------------------

export interface UpdateIntegrationsPayload {
  financial: {
    product: FinancialProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
  };
  hr: {
    product: HRProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
  };
  project: {
    product: ProjectProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
    externalInstanceIdSecondary?: string | null;
  };
  planner: {
    product: PlannerProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
  };
  timesheet: {
    product: TimesheetProduct | null;
    externalInstanceId: string | null;
    instanceName?: string | null;
  };
}

export interface TestIntegrationResponse {
  success: boolean;
  message: string;
  testedAt: string;
}
